/*
 * © 2026 Snyk Limited
 *
 * Licensed under the Apache License, Version 2.0 (the 'License');
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an 'AS IS' BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NoSupportedFilesError = exports.ForbiddenError = exports.InternalError = void 0;
const types_1 = require("../types");
const problem_error_1 = require("../problem-error");
/**
 * @class
 * @name InternalError
 * @description An unexpected error occurred in the AIBOM request. Review the request while providing the debug command flag `-d`. If the error persists, contact Snyk Support.
 * @summary Unexpected error
 * @category AiBom
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class InternalError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Unexpected error',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-aibom-0001',
            status: 500,
            description: `An unexpected error occurred in the AIBOM request. Review the request while providing the debug command flag \`-d\`. If the error persists, contact Snyk Support.`,
            errorCode: 'SNYK-AIBOM-0001',
            level: 'error',
            classification: types_1.Classification.UNEXPECTED,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.InternalError = InternalError;
/**
 * @class
 * @name ForbiddenError
 * @description You or your Organization do not have permission to use this AIBOM feature. Check your user permissions or contact Snyk support.
 * @summary Forbidden
 * @category AiBom
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class ForbiddenError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Forbidden',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-aibom-0002',
            status: 403,
            description: `You or your Organization do not have permission to use this AIBOM feature. Check your user permissions or contact Snyk support.`,
            errorCode: 'SNYK-AIBOM-0002',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.ForbiddenError = ForbiddenError;
/**
 * @class
 * @name NoSupportedFilesError
 * @description Snyk was unable to find any supported files for the AIBOM command. Ensure the directory you are scanning contains supported files.
 * @summary No supported files
 * @category AiBom
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class NoSupportedFilesError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'No supported files',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-aibom-0003',
            status: 422,
            description: `Snyk was unable to find any supported files for the AIBOM command. Ensure the directory you are scanning contains supported files.`,
            errorCode: 'SNYK-AIBOM-0003',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.NoSupportedFilesError = NoSupportedFilesError;
//# sourceMappingURL=AiBom-error-catalog.js.map